/**
 * Gmail OAuth — PKCE flow for browser PWAs.
 *
 * Two execution paths:
 *   1. mock     -> no auth, returns mock-sourced data
 *   2. paste    -> user obtains a refresh-token offline (Aditya power-user path,
 *                  vault has gmail_token.json) and pastes into the connector.
 *                  We store it encrypted via chassis crypto.
 *   3. pkce     -> full browser PKCE redirect, requires Cloudflare Pages Function
 *                  proxy at /-/oauth/google. Wired in v1.1; today returns NotReady.
 *
 * Token is never persisted in plaintext. Encrypted via passkey-derived KEK.
 *
 * Required scopes for our parsers (additive, all read-only):
 *   gmail.readonly        — orders, statements, OTPs
 *   calendar.readonly     — events
 *   contacts.readonly     — People API
 *   photoslibrary.readonly — closet photos metadata
 */

export interface OAuthToken {
  readonly access_token: string;
  readonly refresh_token?: string;
  readonly expiry: number;
  readonly scopes: readonly string[];
  readonly issued_at: number;
}

export type OAuthMode = 'mock' | 'paste' | 'pkce';

export const GOOGLE_SCOPES = {
  gmail: 'https://www.googleapis.com/auth/gmail.readonly',
  calendar: 'https://www.googleapis.com/auth/calendar.readonly',
  contacts: 'https://www.googleapis.com/auth/contacts.readonly',
  photos: 'https://www.googleapis.com/auth/photoslibrary.readonly',
  fit_activity: 'https://www.googleapis.com/auth/fitness.activity.read',
  fit_sleep: 'https://www.googleapis.com/auth/fitness.sleep.read',
} as const;

const TOKEN_URL = 'https://oauth2.googleapis.com/token';

export interface GmailOAuthOpts {
  readonly client_id: string;
  readonly client_secret?: string;
  readonly scopes: readonly string[];
}

export class GmailOAuth {
  constructor(private readonly opts: GmailOAuthOpts) {}

  /** Refresh an existing refresh-token. Used after user pastes credential JSON. */
  async refreshAccessToken(refresh_token: string): Promise<OAuthToken> {
    const body = new URLSearchParams();
    body.set('client_id', this.opts.client_id);
    if (this.opts.client_secret) body.set('client_secret', this.opts.client_secret);
    body.set('refresh_token', refresh_token);
    body.set('grant_type', 'refresh_token');

    const r = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`google token refresh failed (${r.status}): ${text.slice(0, 240)}`);
    }
    const json = (await r.json()) as {
      access_token: string;
      expires_in: number;
      scope: string;
      token_type: string;
    };
    const now = Date.now();
    return {
      access_token: json.access_token,
      refresh_token,
      expiry: now + json.expires_in * 1000,
      scopes: json.scope.split(/\s+/),
      issued_at: now,
    };
  }

  /** Build the consent-screen URL for PKCE flow. v1.1. */
  buildAuthorizeUrl(redirect_uri: string, code_challenge: string, state: string): string {
    const u = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    u.searchParams.set('client_id', this.opts.client_id);
    u.searchParams.set('redirect_uri', redirect_uri);
    u.searchParams.set('response_type', 'code');
    u.searchParams.set('scope', this.opts.scopes.join(' '));
    u.searchParams.set('access_type', 'offline');
    u.searchParams.set('prompt', 'consent');
    u.searchParams.set('code_challenge', code_challenge);
    u.searchParams.set('code_challenge_method', 'S256');
    u.searchParams.set('state', state);
    u.searchParams.set('include_granted_scopes', 'true');
    return u.toString();
  }

  static async generatePkcePair(): Promise<{ verifier: string; challenge: string }> {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const verifier = base64Url(bytes);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    const challenge = base64Url(new Uint8Array(digest));
    return { verifier, challenge };
  }

  async exchangePkceCode(code: string, redirect_uri: string, verifier: string): Promise<OAuthToken> {
    const body = new URLSearchParams();
    body.set('client_id', this.opts.client_id);
    if (this.opts.client_secret) body.set('client_secret', this.opts.client_secret);
    body.set('code', code);
    body.set('redirect_uri', redirect_uri);
    body.set('grant_type', 'authorization_code');
    body.set('code_verifier', verifier);

    const r = await fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!r.ok) throw new Error(`google code exchange failed (${r.status})`);
    const json = (await r.json()) as {
      access_token: string;
      refresh_token?: string;
      expires_in: number;
      scope: string;
    };
    const now = Date.now();
    return {
      access_token: json.access_token,
      ...(json.refresh_token ? { refresh_token: json.refresh_token } : {}),
      expiry: now + json.expires_in * 1000,
      scopes: json.scope.split(/\s+/),
      issued_at: now,
    };
  }
}

function base64Url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i] ?? 0);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/** Helper -- check if a token is fresh; if not, refresh in-place. */
export async function ensureFreshToken(
  oauth: GmailOAuth,
  token: OAuthToken,
  buffer_ms = 60_000,
): Promise<OAuthToken> {
  if (Date.now() < token.expiry - buffer_ms) return token;
  if (!token.refresh_token) throw new Error('token expired and no refresh_token');
  return oauth.refreshAccessToken(token.refresh_token);
}

export async function gmailFetch<T>(
  token: OAuthToken,
  url: string,
  init?: RequestInit,
): Promise<T> {
  const r = await fetch(url, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      authorization: `Bearer ${token.access_token}`,
    },
  });
  if (!r.ok) throw new Error(`google api ${r.status} on ${new URL(url).pathname}`);
  return (await r.json()) as T;
}
